import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/apiClient";
import { Loader2, Home, Sofa, ArrowRight, Heart, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function RedesignShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [voting, setVoting] = useState(-1);

  const load = useCallback(() => {
    api.get(`/public/redesigns/${shareId}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [shareId]);

  useEffect(() => { load(); }, [load]);

  const votedKey = (i) => `archimesh_vote_${shareId}_${i}`;
  const hasVoted = (i) => { try { return !!localStorage.getItem(votedKey(i)); } catch { return false; } };

  const vote = async (i) => {
    if (hasVoted(i)) { toast.info("You already voted for this option"); return; }
    setVoting(i);
    try {
      const { data: res } = await api.post(`/public/redesigns/${shareId}/vote`, { index: i });
      try { localStorage.setItem(votedKey(i), "1"); } catch {}
      setData((d) => ({ ...d, votes: res.votes }));
      toast.success("Vote counted \u2014 thanks!");
    } catch { toast.error("Could not record vote"); }
    finally { setVoting(-1); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1117] text-[#8B949E]" data-testid="redesign-share-error">
        This redesign link is no longer available.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1117]">
        <Loader2 className="w-8 h-8 animate-spin text-[#38BDF8]" />
      </div>
    );
  }

  const isExt = data.kind === "exterior";
  const accent = isExt ? "#38BDF8" : "#F59E0B";
  const label = isExt ? "Exterior Redesign" : `Interior Design${data.space ? ` \u2014 ${data.space}` : ""}`;
  const variations = data.variations && data.variations.length ? data.variations : [data.result_path];
  const votes = data.votes && data.votes.length ? data.votes : variations.map(() => 0);
  const maxVotes = Math.max(0, ...votes);
  const isGallery = variations.length > 1;

  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]" data-testid="redesign-share-page">
      <header className="border-b border-[#21262D] px-6 py-4 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          {isExt ? <Home className="w-4 h-4" /> : <Sofa className="w-4 h-4" />}
        </div>
        <span className="font-heading font-bold tracking-tight">ArchiMesh<span style={{ color: accent }}>AI</span></span>
        <span className="ml-auto text-xs uppercase tracking-widest text-[#6E7681]">{label} · {String(data.style || "").replace(/^\w/, (c) => c.toUpperCase())}</span>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
          {isGallery ? "Vote for your favourite" : "Before & After"}
        </h1>
        <p className="text-[#8B949E] text-sm mb-8">
          {isGallery
            ? `An AI ${isExt ? "exterior remodel" : "interior design"} in ${variations.length} concepts — tap the heart on the one you love.`
            : `An AI ${isExt ? "exterior remodel" : "interior design"} concept generated with ArchiMesh AI.`}
        </p>

        {/* Original */}
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-widest text-[#6E7681] mb-2">Original</p>
          <figure className="rounded-2xl overflow-hidden border border-[#21262D] max-w-2xl">
            <img src={fileUrl(data.original_path)} alt="Original" className="w-full object-cover" data-testid="share-before-img" />
          </figure>
        </div>

        {/* Options */}
        <p className="text-[10px] uppercase tracking-widest text-[#6E7681] mb-3">{isGallery ? "Concepts" : "Redesigned"}</p>
        <div className={`grid gap-5 ${isGallery ? "sm:grid-cols-2 lg:grid-cols-3" : "max-w-2xl"}`}>
          {variations.map((p, i) => {
            const leader = isGallery && votes[i] === maxVotes && maxVotes > 0;
            return (
              <figure key={p} className="rounded-2xl overflow-hidden border-2 bg-[#0B0F14] flex flex-col" style={{ borderColor: leader ? accent : "#21262D" }} data-testid={`gallery-option-${i}`}>
                <img src={fileUrl(p)} alt={`Option ${i + 1}`} className="w-full object-cover" data-testid={i === 0 && !isGallery ? "share-after-img" : `option-img-${i}`} />
                <figcaption className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[#21262D]">
                  <span className="text-xs font-semibold flex items-center gap-1" style={{ color: leader ? accent : "#8B949E" }}>
                    {leader && <Trophy className="w-3.5 h-3.5" />} Option {String.fromCharCode(65 + i)}
                  </span>
                  {isGallery && (
                    <button onClick={() => vote(i)} disabled={voting === i || hasVoted(i)}
                      className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full border transition-colors disabled:opacity-60"
                      style={{ borderColor: accent, color: accent }} data-testid={`vote-btn-${i}`}>
                      {voting === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" fill={hasVoted(i) ? accent : "none"} />}
                      <span data-testid={`vote-count-${i}`}>{votes[i]}</span>
                    </button>
                  )}
                </figcaption>
              </figure>
            );
          })}
        </div>

        <a href="/" className="inline-flex items-center gap-2 mt-10 text-sm font-semibold hover:opacity-80 transition-opacity" style={{ color: accent }} data-testid="share-cta">
          Create your own with ArchiMesh AI <ArrowRight className="w-4 h-4" />
        </a>
      </main>
    </div>
  );
}
